import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Button, Checkbox, Input, Select } from '@mantine/core';
import { useSelector } from 'react-redux';
import base from '../../apis';
import { toast } from 'react-toastify';
import Spinner from '../../components/Spinner/Spinner';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '20px',
  },
}));

const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const timezones = [
  'US/Alaska',
  'US/Aleutian',
  'US/Arizona',
  'US/Central',
  'US/East-Indiana',
  'US/Eastern',
  'US/Hawaii',
  'US/Indiana-Starke',
  'US/Michigan',
  'US/Mountain',
  'US/Pacific',
  'US/Samoa',
];

const returnTime = (time) => (time?.length > 0 ? time : [{ start_time: '', end_time: '' }]);

function Availability() {
  const classes = useStyles();
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [selectedTimes, setSelectedTimes] = useState({
    sunday: {
      show: false,
      dates: [{ start_time: '', end_time: '' }],
    },
    monday: {
      show: false,
      dates: [{ start_time: '', end_time: '' }],
    },
    tuesday: {
      show: false,
      dates: [{ start_time: '', end_time: '' }],
    },
    wednesday: {
      show: false,
      dates: [{ start_time: '', end_time: '' }],
    },
    thursday: {
      show: false,
      dates: [{ start_time: '', end_time: '' }],
    },
    friday: {
      show: false,
      dates: [{ start_time: '', end_time: '' }],
    },
    saturday: {
      show: false,
      dates: [{ start_time: '', end_time: '' }],
    },
  });
  const [first, setFirst] = useState(true);
  const id = useSelector((state) => state.auth.marketerDetails.id);
  const [isLoading, setIsLoading] = useState(false);

  const getDetails = useCallback(() => {
    setIsLoading(true);
    base
      .get(`/operations/availability/${id}/`)
      .then(({ data }) => {
        if (data.marketer) {
          setFirst(false);
        }
        setSelectedTimezone(data.timezone);
        const { monday, tuesday, wednesday, thursday, friday, saturday, sunday } = data;
        setSelectedTimes({
          monday: {
            dates: returnTime(monday),
            show: monday.length > 0,
          },
          tuesday: {
            dates: returnTime(tuesday),
            show: tuesday.length > 0,
          },
          wednesday: {
            dates: returnTime(wednesday),
            show: wednesday.length > 0,
          },
          thursday: {
            dates: returnTime(thursday),
            show: thursday.length > 0,
          },
          friday: {
            dates: returnTime(friday),
            show: friday.length > 0,
          },
          saturday: {
            dates: returnTime(saturday),
            show: saturday.length > 0,
          },
          sunday: {
            dates: returnTime(sunday),
            show: sunday.length > 0,
          },
        });
        // setSelectedTimes(data.days);
      })
      .catch((err) => {
        toast.warn('Could not get times');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  useEffect(() => {
    getDetails();
  }, [getDetails]);

  const onAddNewTime = (day) => {
    setSelectedTimes((prev) => ({
      ...prev,
      [day]: { ...prev[day], dates: [...prev[day].dates, { start_time: '', end_time: '' }] },
    }));
  };

  const onRemoveTime = (day, index) => {
    setSelectedTimes((prev) => ({
      ...prev,
      [day]: { ...prev[day], dates: prev[day].dates.filter((_, i) => i !== index) },
    }));
    const idToDelete = selectedTimes[day].dates[index]?.id;
    if (idToDelete) onDelete([idToDelete]);
  };

  const handleCheckboxChange = (e) => {
    const { name } = e.target;
    if (selectedTimes[name].show) {
      const toDel = selectedTimes[name].dates.map((item) => item.id).filter(Boolean);
      onDelete(toDel);
      setSelectedTimes((prev) => ({
        ...prev,
        [name]: { ...prev[name], show: false, dates: [{ start_time: '', end_time: '' }] },
      }));
    } else {
      setSelectedTimes((prev) => ({
        ...prev,
        [name]: { ...prev[name], show: true },
      }));
    }
  };

  const onDelete = (array_time) => {
    base
      .post('/operations/time/', { array_time })
      .then(({ data }) => {})
      .catch((err) => {
        toast.error('Could not delete time');
      });
  };

  const saveTimes = () => {
    if (!selectedTimezone) {
      toast.warn('Please select a timezone');
      return;
    }
    const body = {
      timezone: selectedTimezone,
      marketer_id: id,
    };
    Object.keys(selectedTimes).forEach((item) => {
      body[item] = selectedTimes[item].show ? selectedTimes[item].dates : [];
    });
    setIsLoading(true);
    const call = first
      ? base.post(`/operations/availability/`, body)
      : base.put(`/operations/availability/${id}/`, body);
    call
      .then(({ data }) => {
        getDetails();
      })
      .catch((err) => {
        toast.error('Could not save times');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onDateChanged = (e, day, index, type) => {
    const dates = selectedTimes[day].dates;
    dates[index][type] = e.target.value;

    setSelectedTimes((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        dates: dates,
      },
    }));
  };

  return (
    <>
      {isLoading && <Spinner />}
      <div className={classes.root}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Select
            label="Timezone"
            placeholder="Select timezone"
            data={timezones}
            value={selectedTimezone}
            onChange={setSelectedTimezone}
            sx={{ width: '200px' }}
          />
          <Button onClick={saveTimes}>Save</Button>
        </Box>
        <Box marginTop="10px">
          {days.map((day, dayIndex) => (
            <Box display="flex" borderBottom="1px solid gray" key={day} marginTop="10px" padding="10px" gap="10px">
              <Checkbox
                label={<span style={{ textTransform: 'capitalize' }}>{day}</span>}
                sx={{ marginTop: '7px', width: '120px' }}
                checked={selectedTimes[day].show}
                name={day}
                onChange={handleCheckboxChange}
              />
              <Box display="flex" gap="10px">
                <Box display="flex" flexDirection="column">
                  {selectedTimes[day].dates.map((date, index) => (
                    <Box display="flex" gap="10px" key={index}>
                      <Input
                        type="time"
                        value={date.start_time}
                        onChange={(e) => onDateChanged(e, day, index, 'start_time')}
                        disabled={!selectedTimes[day].show}
                      />

                      <p style={{ marginTop: '7px' }}>-</p>
                      <Input
                        type="time"
                        disabled={!selectedTimes[day].show}
                        value={date.end_time}
                        onChange={(e) => onDateChanged(e, day, index, 'end_time')}
                      />

                      {selectedTimes[day].dates.length > 1 && (
                        <DeleteIcon
                          fontSize="small"
                          sx={{ cursor: 'pointer', marginTop: '7px', color: 'gray' }}
                          onClick={() => onRemoveTime(day, index)}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
              <AddIcon
                fontSize="small"
                sx={{ marginLeft: 'auto', cursor: 'pointer', marginTop: '7px' }}
                onClick={() => onAddNewTime(day)}
              />
            </Box>
          ))}
        </Box>
      </div>
    </>
  );
}

export default Availability;
